import { useCallback } from 'react';

import { requestBuilder } from '../../http/RequestBuilder';
import type { GuidanceSessionType } from '../types/GuidanceSessions';
import { useAfterBookingGuidanceSession } from './useAfterBookingGuidanceSession';

interface BookGuidanceSessionBody {
    guideId: string;
    sessionType: GuidanceSessionType;
    startTimeUtc: string;
    userTimeZone: string;
}

export interface BookGuidanceSessionResponse {
    calendlyEventUri: string;
    cancelUrl: string;
    rescheduleUrl: string;
    rescheduledSessionId?: string;
    existingSessionsCancelled?: boolean;
    isFirstKickoffSession: boolean;
}

const usePostBookGuidanceSession = requestBuilder.mutation<
    BookGuidanceSessionResponse,
    BookGuidanceSessionBody
>({
    endpoint: '/api/guidance/sessions/book',
    method: 'POST',
});

export const useBookGuidanceSession = () => {
    const { mutateAsync, isPending, isSuccess, isError, error } =
        usePostBookGuidanceSession();
    const { afterBookingGuidanceSession } = useAfterBookingGuidanceSession();

    const bookGuidanceSession = useCallback(
        async (params: BookGuidanceSessionBody) => {
            const booking = await mutateAsync({
                body: params,
            });
            // eslint-disable-next-line promise/avoid-new
            await new Promise((resolve) => {
                setTimeout(resolve, 3000); // Allow Calendly webhook time to process
            });
            await afterBookingGuidanceSession(
                params.sessionType,
                booking?.isFirstKickoffSession,
            );
        },
        [mutateAsync, afterBookingGuidanceSession],
    );

    return {
        bookGuidanceSession,
        isBookingGuidanceSession: isPending,
        isBookGuidanceSessionSuccess: isSuccess,
        isBookGuidanceSessionError: isError,
        bookGuidanceSessionError: error,
    };
};
